/**
 * A clinic phone number as a tap-to-call link.
 *
 * Before this, location cards printed the number as plain text: not tappable
 * on a phone, and invisible to tracking. The click is counted by the global
 * tel: listener in src/lib/utm-tracking.ts (Zaraz event "phone_click").
 */
export function telHref(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  const e164 = digits.length === 10 ? `1${digits}` : digits;
  return `tel:+${e164}`;
}


/**
 * Why phone links carry `suppressHydrationWarning`.
 *
 * CallRail's swap.js replaces the main number with a tracking number in the
 * page itself: it rewrites the link's `href` and the text node holding the
 * number, in place. It runs as soon as a visitor has given marketing consent,
 * which can be before React has hydrated the link. React would then find text
 * that differs from what it rendered, report error 418 and re-render the page
 * on the client.
 *
 * With `suppressHydrationWarning`, React keeps the text and attributes that are
 * already in the page for that one element. It reaches one level deep only: the
 * element's own attributes and its own text, not its child elements. So it
 * belongs on the element whose text is the number, and never on a container.
 *
 * The words before the number ("Call ", "Call us: ") are a separate text node
 * of the same element, so the attribute covers both.
 *
 * Every element that shows the main number needs it. A number inside a longer
 * sentence needs its own element (this link) for the same reason.
 */
export function PhoneLink({
  phone,
  className,
  prefix = "Call ",
}: {
  phone: string;
  className?: string;
  prefix?: string;
}) {
  return (
    <a href={telHref(phone)} className={className ?? "hover:underline"} suppressHydrationWarning>
      {/* The verb and the number are two text nodes of this one element. The
          link's spoken name is its text, so it reads "Call (866) ..." with
          whatever number is showing. swap.js rewrites only the node that holds
          the number and the href. */}
      {prefix}
      {phone}
    </a>
  );
}
